import { useId, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// ─── The shared parts of the calendar's write dialogs ──────────────────────────
//
// Extracted from NewBookingDialog so the empty-slot menu, the block-time dialog
// and the memo dialog present a choice identically. That matters beyond looks:
// these dialogs are the calendar's user-granted exception to the chat-first
// rule, and the line that contains them is that an input is a CHOICE from a
// server-supplied list rather than a value the operator types. One shared
// ChoiceButton is what keeps that visible — a screen that starts styling its own
// buttons is a screen drifting towards a form.
//
// Free-text inputs do exist in two places (a client's name and contact details,
// a closure's reason, a memo's text) and each states at its own call site why it
// cannot be a choice.

/** One tappable choice. */
export function ChoiceButton({
  title,
  subtitle,
  selected,
  busy,
  disabled,
  onClick,
}: {
  title: string
  subtitle?: string
  selected?: boolean
  busy?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        selected
          ? 'border-primary bg-primary/5 hover:bg-primary/10'
          : 'border-border bg-card hover:bg-muted',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-foreground">{title}</span>
        {subtitle ? (
          <span className="mt-px block truncate text-[11.5px] text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
      {busy ? <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden /> : null}
      {selected && !busy ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
    </button>
  )
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
      {children}
    </span>
  )
}

/** The primary confirm button at the foot of a slot dialog. */
export function ConfirmButton({
  label,
  icon,
  busy,
  disabled,
  onClick,
}: {
  label: string
  icon: ReactNode
  busy?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5',
        'text-[14px] font-semibold text-primary-foreground',
        'bg-primary hover:bg-primary/90',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {label}
    </button>
  )
}

/**
 * One row in a dialog's action list — icon, label, one-line hint. A whole
 * decision, never a field; the dialog cousin of the booking sheet's ActionRow.
 */
export function ActionButtonRow({
  icon,
  label,
  hint,
  disabled,
  busy,
  onClick,
}: {
  icon: ReactNode
  label: string
  hint?: string
  disabled?: boolean
  busy?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors',
        'hover:bg-muted disabled:cursor-not-allowed disabled:opacity-45',
      )}
    >
      <span className="shrink-0" aria-hidden>
        {busy ? <Loader2 className="size-4 animate-spin" /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-foreground">{label}</span>
        {hint ? <span className="mt-px block truncate text-[11.5px] text-muted-foreground">{hint}</span> : null}
      </span>
    </button>
  )
}

/**
 * A single-line text field.
 *
 * The hint is wired with `aria-describedby` and deliberately NOT wrapped inside
 * the `<label>`. A wrapping label folds every descendant into the accessible
 * name, so "Mobile" would be announced as "Mobile, required — it is how texts
 * and reminders reach them" — the whole sentence read out as the field's name,
 * every time focus lands. Described-by is announced after the name and only
 * once.
 */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus,
  hint,
  inputMode,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: 'text' | 'email' | 'tel' | 'date' | 'number'
  autoFocus?: boolean
  hint?: string
  inputMode?: 'text' | 'tel' | 'email' | 'numeric'
}) {
  const id = useId()
  const hintId = `${id}-hint`
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <label
        htmlFor={id}
        className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        autoFocus={autoFocus}
        aria-describedby={hint ? hintId : undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
      />
      {hint ? <span id={hintId} className="text-[11px] text-muted-foreground">{hint}</span> : null}
    </div>
  )
}
